import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { ComfortApiService } from 'src/app/services/comfort-api.service';
import { StorageService } from 'src/app/services/storage.service';
import { TemperatureService } from 'src/app/services/temperature.service';
import { ToastService } from 'src/app/services/toast.service';
import { WindowPayloadBuilderService } from 'src/app/services/window-payload-builder.service';

@Component({
  selector: 'app-comfort-calculator',
  templateUrl: './comfort-calculator.component.html',
  styleUrls: ['./comfort-calculator.component.scss'],
  standalone: false,
})
export class ComfortCalculatorComponent implements OnInit {
  storedProject: any = {};
  _internalTe: number = -5;
  _internalTi: number = 20;
  ePhi: number = 50;
  minTe = -30; // Minimum value
  maxTe = 50; // Maximum value
  minTi = -30;
  maxTi = 50;
  minPhi = 10;
  maxPhi = 90;
  stepCount = 1; // Increment/decrement step

  isFormValid = false;
  isLoading = false;
  hasError = false;
  errorMessage = '';

  // Calculation results
  resSurfaceTemp!: number;
  resDewPoint!: number;
  resMoldTemp!: number;
  resCondensationMsg: string = "";
  resMouldMsg: string = "";
  temperatureUnit: 'celsius' | 'fahrenheit' = 'celsius';
  // Track previous values for comparison
  private previousValues = {
    eTe: null as number | null,
    eTi: null as number | null,
    ePhi: null as number | null
  };
  // Create a Subject for debouncing
  private calculationTrigger$ = new Subject<{ eTe: number, eTi: number, ePhi: number }>();
  private calculationSubscription: Subscription;

  constructor(private modalController: ModalController, private storageService: StorageService,
    private comfortApiService: ComfortApiService, private windowPayloadBuilder: WindowPayloadBuilderService,
    private toastService: ToastService, private translate: TranslateService, private temperatureService: TemperatureService
  ) {

    // Subscribe to debounced calculation trigger
    this.calculationSubscription = this.calculationTrigger$
      .pipe(
        debounceTime(500), // Wait 500ms after last change
        distinctUntilChanged((prev, curr) =>
          prev.eTe === curr.eTe &&
          prev.eTi === curr.eTi &&
          prev.ePhi === curr.ePhi
        )
        // distinctUntilChanged() // Only trigger if values changed
      )
      .subscribe(() => {
        this.performCalculations();
      });
  }

  ngOnInit() { }

  ionViewWillEnter() {
    this.init();
  }

  private async init(): Promise<void> {
    try {
      await this.loadUserSettings();
      const res = await this.storageService.getProject();
      this.storedProject = res ? JSON.parse(res) : null;
      // Initial calculation
      await this.loadCalculatorData();
      this.validateForm();
    } catch (error) {
      console.error('Error initializing spacer data:', error);
    }
  }
  async loadUserSettings() {
    this.temperatureUnit = await this.temperatureService?.getTemperatureUnit();
    if (this.temperatureUnit === 'fahrenheit') {
      this.minTe = -22; this.maxTe = 122;
      this.minTi = -22; this.maxTi = 122;
    } else {
      this.minTe = -30; this.maxTe = 50;
      this.minTi = -30; this.maxTi = 50;
    }
  }
  private async loadCalculatorData(): Promise<void> {
    try {
      const res = await this.storageService.getProject();
      const storedProject = res ? JSON.parse(res) : null;

      if (storedProject?.comfortCalculator) {
        const savedData = storedProject.comfortCalculator;
        this._internalTe = savedData?.te
        this._internalTi = savedData?.ti
        this.ePhi = savedData?.phi
      } else {
        // No saved data 
      }
      await this.performCalculations();
    } catch (error) {
      console.error('Error loading basic data for season:', error);
    }
  }

  ngOnDestroy() {
    // Clean up subscription
    if (this.calculationSubscription) {
      this.calculationSubscription.unsubscribe();
    }
  }

  // Add these methods to validate and reset values
  // private validateAndResetValues() {
  //   // Validate eTe
  //   if (this.eTe > this.maxTe) {
  //     this.eTe = this.maxTe;
  //   } else if (this.eTe < this.minTe) {
  //     this.eTe = this.minTe;
  //   } else if (this.eTe === null || this.eTe === undefined || isNaN(this.eTe)) {
  //     this.eTe = 0;
  //   }


  //   // Validate eTi
  //   if (this.eTi > this.maxTi) {
  //     this.eTi = this.maxTi;
  //   } else if (this.eTi < this.minTi) {
  //     this.eTi = this.minTi;
  //   } else if (this.eTi === null || this.eTi === undefined || isNaN(this.eTi)) {
  //     this.eTi = 0; // Default value
  //   }

  //   // Validate ePhi
  //   if (this.ePhi > this.maxPhi) {
  //     this.ePhi = this.maxPhi;
  //   } else if (this.ePhi < this.minPhi) {
  //     this.ePhi = this.minPhi;
  //   } else if (this.ePhi === null || this.ePhi === undefined || isNaN(this.ePhi)) {
  //     this.ePhi = 10; // Default value
  //   }

  // }
  // DISPLAY getters/setters (Fahrenheit when toggled)
  get eTe(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? this.temperatureService.celsiusToFahrenheit(this._internalTe)
      : this._internalTe;
  }

  set eTe(value: number) {
    this._internalTe = this.temperatureUnit === 'fahrenheit'
      ? this.temperatureService.fahrenheitToCelsius(value)
      : value;
  }

  get eTi(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? this.temperatureService.celsiusToFahrenheit(this._internalTi)
      : this._internalTi;
  }

  set eTi(value: number) {
    this._internalTi = this.temperatureUnit === 'fahrenheit'
      ? this.temperatureService.fahrenheitToCelsius(value)
      : value;
  }
  get displayResSurfaceTemp(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? parseFloat(this.temperatureService.celsiusToFahrenheit(this.resSurfaceTemp ?? 0).toFixed(3))
      : this.resSurfaceTemp ?? 0;
  }

  get displayResDewPoint(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? parseFloat(this.temperatureService.celsiusToFahrenheit(this.resDewPoint ?? 0).toFixed(3))
      : this.resDewPoint ?? 0;
  }

  get displayResMoldTemp(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? parseFloat(this.temperatureService.celsiusToFahrenheit(this.resMoldTemp ?? 0).toFixed(3))
      : this.resMoldTemp ?? 0;
  }

  get tempUnitSymbol(): string {
    return this.temperatureUnit === 'fahrenheit' ? '°F' : '°C';
  }


  incrementTe() {
    if (this.eTe < this.maxTe) {
      this.eTe = Math.min(this.maxTe, this.eTe + this.stepCount);
      this.validateForm();
    }
  }
  decrementTe() {
    if (this.eTe > this.minTe) {
      this.eTe = Math.max(this.minTe, this.eTe - this.stepCount);
      this.validateForm();
    }
  }

  incrementTi() {
    if (this.eTi < this.maxTi) {
      this.eTi = Math.min(this.maxTi, this.eTi + this.stepCount);
      this.validateForm();
    }
  }
  decrementTi() {
    if (this.eTi > this.minTi) {
      this.eTi = Math.max(this.minTi, this.eTi - this.stepCount);
      this.validateForm();
    }
  }

  incrementPhi() {
    if (this.ePhi < this.maxPhi) {
      this.ePhi = Math.min(this.maxPhi, this.ePhi + this.stepCount);
      this.validateForm();
    }
  }
  decrementPhi() {
    if (this.ePhi > this.minPhi) {
      this.ePhi = Math.max(this.minPhi, this.ePhi - this.stepCount);
      this.validateForm();
    }
  }
  async performCalculations() {
    console.log("call API")
    // Only call API if form is valid
    if (!this.isFormValid) {
      return;
    }

    try {

      // Call your API here
      const result = await this.callCalculationApi();

      // Update UI with results
      this.updateResults(result);
    } catch (error) {
      console.error('Error performing calculations:', error);
    }
  }

  private async callCalculationApi(): Promise<any> {
    // let data = {
    //   "te": this.eTe,
    //   "ti": this.eTi,
    //   "phi": this.ePhi
    // }
    // Save the new comfortCalculator data
    // await this.storageService.updateProject({
    //   comfortCalculator: data
    // });
    const apiParam = {
      "windowConfiguration": (await this.windowPayloadBuilder.buildWindowPayloads())?.window1,
      "te": this._internalTe,
      "ti": this._internalTi,
      "phi": this.ePhi
    };

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    try {
      const response: any = await this.comfortApiService.calculateComfort(apiParam).toPromise();

      if (response) {
        this.resSurfaceTemp = response.surfaceTemperature;
        this.resDewPoint = response.dewPoint;
        this.resMoldTemp = response.moldTemperature;
        if (this.resSurfaceTemp <= this.resDewPoint) {
          this.resCondensationMsg = "WARNING_CONDENSATION"
        } else {
          this.resCondensationMsg = "NO_CONDENSATION"
        }
        if (this.resSurfaceTemp <= this.resMoldTemp) {
          this.resMouldMsg = "WARNING_MOULD"
        } else {
          this.resMouldMsg = "NO_MOULD"
        }
      } else {
        throw new Error(response.message || 'Calculation failed');
      }

    } catch (error: any) {
      console.error('Calculation error:', error);
      this.hasError = true;
      this.errorMessage = error.message || 'Failed to calculate comfort values. Please try again.';

    } finally {
      this.isLoading = false;
    }
  }

  private updateResults(result: any) {
    console.log("result", result)
  }

  async validateForm(): Promise<void> {
    console.log("validate form called")
    // First validate and reset values to ensure they're within limits
    // this.validateAndResetValues();

    // Validate ranges explicitly
    const isEteValid = this.eTe >= this.minTe && this.eTe <= this.maxTe;
    const isEtiValid = this.eTi >= this.minTi && this.eTi <= this.maxTi;
    const isEPhiValid = this.ePhi >= this.minPhi && this.ePhi <= this.maxPhi;
    const errors: string[] = [];

    if (!isEteValid) {
      errors.push(
        this.translate.instant('ERROR_TE_RANGE', {
          min: this.minTe,
          max: this.maxTe
        })
      );
    }

    if (!isEtiValid) {
      errors.push(
        this.translate.instant('ERROR_TI_RANGE', {
          min: this.minTi,
          max: this.maxTi
        })
      );
    }

    if (!isEPhiValid) {
      errors.push(
        this.translate.instant('ERROR_PHI_RANGE', {
          min: this.minPhi,
          max: this.maxPhi
        })
      );
    }

    if (errors.length > 0) {
      const message = errors.map(e => `${e}. `).join('\n');

      this.errorMessage = message;

      await this.toastService.show(
        this.translate.instant(message),
        3000,
        'bottom'
      );

    }
    this.isFormValid = isEteValid && isEtiValid && isEPhiValid;
    console.log("this.isFormValid", this.isFormValid)

    // Check if required fields have values
    // const hasEte = this.eTe != null && this.eTe != undefined && !isNaN(this.eTe);
    // const hasEti = this.eTi != null && this.eTi != undefined && !isNaN(this.eTi);
    // const hasEPhsi = this.ePhi != null && this.ePhi != undefined && !isNaN(this.ePhi) && this.ePhi > 0;

    // this.isFormValid = hasEte && hasEti && hasEPhsi;

    // Only emit if values have changed
    if (
      this.previousValues.eTe !== this.eTe ||
      this.previousValues.eTi !== this.eTi ||
      this.previousValues.ePhi !== this.ePhi
    ) {
      console.log("calculationTrigger next")
      this.calculationTrigger$.next({
        eTe: this.eTe,
        eTi: this.eTi,
        ePhi: this.ePhi
      });

      // Update previous values
      this.previousValues = {
        eTe: this.eTe,
        eTi: this.eTi,
        ePhi: this.ePhi
      };
    }
  }

  async validate() {
    this.validateForm();
    if (!this.isFormValid) return;

    const comfortData = {
      te: this._internalTe,
      ti: this._internalTi,
      phi: this.ePhi,
      surfaceTemperature: this.resSurfaceTemp ?? 0,
      dewPoint: this.resDewPoint ?? 0,
      moldTemperature: this.resMoldTemp ?? 0,
    };

    try {
      await this.storageService.updateProject({
        comfortCalculator: comfortData
      });
      console.log('Comfort calculator saved with results:', comfortData);
    } catch (error) {
      console.error('Error saving comfort calculator:', error);
    }

    this.modalController.dismiss(null, 'confirm');
  }


  close() {
    this.modalController.dismiss();
  }
}
